import React, { useContext, useEffect, useState } from 'react';
import { api } from '../../providers/apiClient';
import { ContextUser } from '../../providers/ContextUser';
import Page from '../../components/page';
import Breadcrumbs from '../../components/layout/breadcrumbs';
import CardFile from '../../components/card-file';
import SkeletonLoading from '../../components/skeleton-loading';
import NoResults from '../../components/no-result';
import { buildOrgLevelBreadcrumbs } from '../../utils/breadcrumbUtils';
import { Col, Row } from 'react-bootstrap';
import {
  Container,
  DocumentContainer,
  LoadingContainer,
  NoResultsContainer,
  HorizontalSplit,
  FileInput,
  FileInputGroup,
  SendButton,
  DeleteButton,
} from './styles';
import { HiOutlineDocumentDuplicate } from 'react-icons/hi2';
import { BsTrash } from 'react-icons/bs';
import { toast } from 'react-toastify';
import { useConfirmToast } from '../../hooks/useConfirmToast';

const Models = () => {
  const { currentOrganization } = useContext(ContextUser);
  const [loading, setLoading] = useState(true);
  const [documentTemplates, setDocumentTemplates] = useState([]);
  const [breadcrumbs, setBreadcrumbs] = useState([]);
  const [fileUpload, setFileUpload] = useState(null);
  const [selected, setSelected] = useState([]);
  const [fileError, setFileError] = useState('');
  const { confirm } = useConfirmToast();

  useEffect(() => {
    fetchOrgModels();
  }, []);

  // Configurar breadcrumbs quando a organização estiver disponível
  useEffect(() => {
    if (currentOrganization?.id) {
      const breadcrumbs = buildOrgLevelBreadcrumbs(
        currentOrganization,
        'Modelos',
        '/models'
      );
      setBreadcrumbs(breadcrumbs);
    }
  }, [currentOrganization]);

  async function fetchOrgModels() {
    setLoading(true);
    const orgId = currentOrganization.id;

    await api
      .get(`/orgs/${orgId}/models`)
      .then((r) => {
        setDocumentTemplates(r.data);
        setSelected([]);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }

  const handleFileUpload = (event) => {
    const file = event.target.files?.[0];
    if (file) {
      const maxSize = 50 * 1024 * 1024; // 50MB
      if (file.size > maxSize) {
        const msg = 'Arquivo excede 50MB. Selecione um arquivo menor.';
        setFileError(msg);
        toast.error(msg);
        event.target.value = '';
        return;
      }
      setFileError('');
      setFileUpload(file);
    }
  };

  const handleUpload = async () => {
    const orgId = currentOrganization.id;
    if (!fileUpload) return;
    const formData = new FormData();
    formData.append('file', fileUpload);

    try {
      const response = await api.post(`/orgs/${orgId}/models`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setDocumentTemplates([
        ...documentTemplates,
        { name: fileUpload.name, url: response.data.url },
      ]);
      toast.success('Upload realizado com sucesso');
      setFileUpload(null);
      setFileError('');
    } catch (error) {
      const message = error?.response?.data?.message || error?.message || 'Erro ao realizar upload';
      toast.error(message);
    }
  };

  const toggleSelect = (name) => {
    setSelected((prev) =>
      prev.includes(name) ? prev.filter((n) => n !== name) : [...prev, name]
    );
  };

  const handleDeleteSelected = () => {
    if (selected.length === 0) return;
    confirm(
      'Os modelos selecionados serão excluídos permanentemente. Deseja continuar?',
      async () => {
        try {
          const orgId = currentOrganization.id;
          await api.delete(`/orgs/${orgId}/models`, { data: { files: selected } });
          toast.success('Modelos removidos com sucesso');
          setSelected([]);
          fetchOrgModels();
        } catch (error) {
          toast.error('Erro ao remover modelos');
        }
      },
      null,
      { confirmText: 'Sim, excluir!', cancelText: 'Cancelar', variant: 'danger' }
    );
  };

  const HeaderDeleteButton = selected.length > 0 ? (
    <DeleteButton key='btnDeleteSelectedModels' onClick={handleDeleteSelected}>
      <BsTrash style={{ marginRight: 8 }} /> Excluir selecionados ({selected.length})
    </DeleteButton>
  ) : null;

  return (
    <>
      <Breadcrumbs id='breadcrumbs-models' path={breadcrumbs} />
      <Page id='page-models' title={'Modelos'} description="Modelos prontos de documentos oficiais para consulta e adaptação." icon={<HiOutlineDocumentDuplicate />} sideAction={[HeaderDeleteButton]}>
        {loading ? (
        <LoadingContainer id='loading-container'>
          <Container id='container-loading'>
            <SkeletonLoading id='models-skel-title' height='24px' width='30%' />
            <HorizontalSplit />
            <Row id='models-skel-grid' style={{ width: '100%' }} className='g-3'>
              {[0,1,2,3,4,5].map((i) => (
                <Col id={`skeleton-models-${i}`} md={4} key={`sk-${i}`}>
                  <SkeletonLoading height='150px' />
                </Col>
              ))}
            </Row>
          </Container>
        </LoadingContainer>
      ) : (
        <Container id='container'>
          <FileInputGroup className='mt-3'>
            <FileInput type='file' onChange={handleFileUpload} />
            <SendButton onClick={handleUpload} disabled={!fileUpload || !!fileError}>Enviar</SendButton>
          </FileInputGroup>
          <div id='models-file-hint' style={{ marginTop: 6, fontSize: 12, color: '#6c757d' }}>
            Tamanho máximo permitido: 50MB.
          </div>
          {fileError && (
            <div id='models-file-error' style={{ marginTop: 4, fontSize: 12, color: '#dc3545' }}>
              {fileError}
            </div>
          )}

          <HorizontalSplit />
          <DocumentContainer id='document-container'>
            {documentTemplates.length > 0
              ? documentTemplates.map((doc) => (
                  <CardFile
                    id={`card-file-${doc.name || doc.id}`}
                    key={doc.url}
                    doc={doc}
                    selectable
                    selected={selected.includes(doc.name)}
                    onToggleSelect={toggleSelect}
                  />
                ))
              : !loading && (
                  <NoResultsContainer id='no-results-container'>
                    <NoResults
                      id='no-results'
                      message='Nenhum modelo encontrado'
                      searchText=''
                    />
                  </NoResultsContainer>
                )}
          </DocumentContainer>
        </Container>
      )}
      </Page>
    </>
  );
};

export default Models;
